import { useState, useEffect, useCallback } from 'react';
import * as _ from 'lodash';

/**
 * A hook which manages if a given state is "dirty" or not,
 * by deeply comparing the "initial value" to the current value.
 *
 * @example
 * @deprecated - use useEditable instead
 * const { isDirty, onResetIsDirty } = useIsDirty(initialState, currentState);
 *
 * <Button
 *   isDisabled={!isDirty}
 *   onClick={async () => {
 *     await onSave();
 *     onResetIsDirty();
 *   }}
 * >
 *   Save
 * </Button>
 */
function useIsDirty<T>(initialState: T, currentState: T) {
  //  We hold a copy of the initial state so that we can expose a method to "reset" it when the user saves.
  const [currentInitialState, setCurrentInitialState] = useState<T>(initialState);

  //  Handle cases where `initialState` actually changes, and update our local copy to compare against.
  //  This is helpful when the data is loading, as the initialState will be null until the fetch is complete.
  //  When it is complete, initialState is changed and this hook runs, updating our local copy (if it's different).
  useEffect(() => {
    if (!_.isEqual(currentInitialState, initialState)) {
      setCurrentInitialState(initialState);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- This is intentional, as we only want to re-set if initialState actually changes (and not currentInitialState).
  }, [initialState]);

  //  Update our local copy of the "initial" state, so that we have something to compare against.
  const onUpdateInitialState = useCallback((newInitialState: T) => setCurrentInitialState(newInitialState), []);

  return {
    isDirty: !_.isEqual(currentInitialState, currentState),
    onUpdateInitialState,
  };
}

export default useIsDirty;
